/**
 * @description ReviewsAccordionItem implementation
 * @param AccordionItem widget
 * @returns Reviews Tab Panel class
 */
function ReviewsAccordionItemClassCreator(AccordionItem: import('widgets/global/AccordionItem').TAccordionItem) {
    /**
     * @category widgets
     * @subcategory product
     * @class ReviewsAccordionItem
     * @augments AccordionItem
     * @classdesc Reviews Accordion Item general behavior.
     */
    class ReviewsAccordionItem extends AccordionItem {
        reviewsMutationObserver?: MutationObserver;

        /**
         * @description Returns Widget configuration object
         * @returns Widget configuration object
         */
        prefs() {
            return {
                variationGroupId: '',
                ...super.prefs()
            };
        }

        /**
         * @description Initialize widget logic
         */
        init() {
            super.init();

            this.eventBus().on('reviews.link.clicked', 'onReviewLinkClick');

            this.initReviewsAutoExpand();
        }

        /**
         * @description Automatically expands custom question content in Yotpo reviews
         */
        initReviewsAutoExpand() {
            const reviewsContainer = document.querySelector('.yotpo-reviews-main-widget');

            if (!reviewsContainer) {
                return;
            }

            this.expandCustomQuestions();

            this.reviewsMutationObserver = new MutationObserver(() => {
                this.expandCustomQuestions();
            });

            this.reviewsMutationObserver.observe(reviewsContainer, {
                childList: true,
                subtree: true
            });
        }

        /**
         * @description Expands Yotpo custom question content
         */
        expandCustomQuestions() {
            const selector = '.yotpo-custom-questions-see-more-label';

            document.querySelectorAll<HTMLElement>(selector).forEach(element => {
                if (element.dataset.autoExpanded) {
                    return;
                }

                element.dataset.autoExpanded = 'true';
                element.click();
            });
        }

        /**
         * @description Send the event to the GTM handler when the review link is clicked and the panel is open
         */
        onReviewLinkClick(reviewsElement, event?: Event) {
            if (this.isPanelOpen) {
                this.eventBus().emit('reviews.click', reviewsElement?.config?.yotpoProductId);
            } else {
                this.openPanel();

                const clickedElement = event?.target;
                const isSummaryLinkClick = clickedElement instanceof Element && clickedElement.closest('.yotpo-star-rating-reviews-summary-link');

                if (isSummaryLinkClick) {
                    return;
                }

                setTimeout(() => {
                    const yotpoWidget = document.querySelector('.yotpo-reviews-main-widget');

                    if (yotpoWidget) {
                        yotpoWidget.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                }, 300);
            }
        }

        /**
         * @description Open panel
         */
        openPanel() {
            super.openPanel();

            const variationGroupID = this.prefs().variationGroupId;

            if (variationGroupID) {
                this.eventBus().emit('reviews.accordion.open', variationGroupID);
            }
        }
    }

    return ReviewsAccordionItem;
}

export type TReviewsAccordionItem = ReturnType<typeof ReviewsAccordionItemClassCreator>;

export type TReviewsAccordionItemInstance = InstanceType<TReviewsAccordionItem>;

export default ReviewsAccordionItemClassCreator;
